import floor from 'lodash/floor';
import moment, { MomentInput } from 'moment';

/**
 * Parse a date-only ISO string (YYYY-MM-DD) as a local calendar date.
 * Avoids the UTC-midnight off-by-one from `new Date('YYYY-MM-DD')`.
 */
export const parseDateOnly = (dateString: string): Date => {
  const [year, month, day] = dateString.split('-').map(Number);
  return new Date(year, month - 1, day);
};

/**
 * Format a Date as a date-only ISO string (YYYY-MM-DD) in local time.
 * Prefer this over `toISOString().slice(0, 10)`, which uses UTC and can
 * shift the calendar day for timezones west of UTC.
 */
export const formatDateOnly = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const offsetToTimeString = (
  minutes: number,
  extendedModifier: boolean = true
): string => {
  const hours = Math.floor(minutes / 60);
  const usTime = hours % 12 || 12;
  const plusDays = floor(hours / 24);

  const usModifier = hours % 24 < 12 ? 'am' : 'pm';

  let modifierStr = usModifier;
  if (extendedModifier && usTime === 12) {
    modifierStr = usModifier === 'pm' ? ' noon' : ' midnight';
  }

  const wrappedMinutes = minutes % 60;
  const minutesStr = wrappedMinutes.toString().padStart(2, '0');
  const getAfterMidnightModifier = () => {
    if (plusDays > 0) {
      return ' (next day)';
    }
    if (plusDays < 0) {
      return ' (previous day)';
    }
    return '';
  };
  const afterMidnightModifier = getAfterMidnightModifier();
  const displayString = `${usTime}:${minutesStr}${modifierStr}${afterMidnightModifier}`;
  return displayString;
};

export const formatEventDate = (
  startDate?: string | null,
  endDate?: string | null
) => {
  let dateString = '';
  const currentYear = moment().format('YYYY');
  if (startDate) {
    const startYear = moment(startDate).format('YYYY');
    if (startYear !== currentYear && startDate === endDate) {
      dateString = moment(startDate).format('ddd, MMM D, YYYY');
    } else if (startDate === endDate) {
      dateString = moment(startDate).format('ddd, MMM D');
    } else {
      dateString = moment(startDate).format('ddd, MMM D');
    }
  }
  if (startDate && endDate && startDate !== endDate) {
    const startMonth = moment(startDate).format('MMM');
    const endMonth = moment(endDate).format('MMM');
    if (startMonth !== endMonth) {
      dateString += ` - ${moment(endDate).format('MMM D')}`;
    } else {
      dateString += `-${moment(endDate).format('D')}`;
    }
    const eventYear = moment(endDate).format('YYYY');
    if (eventYear !== currentYear) {
      dateString += `, ${eventYear}`;
    }
  }
  return dateString;
};

export const formatEventTime = (
  startOffset?: number | null,
  endOffset?: number | null
) => {
  let timeString = '';
  if (startOffset) {
    timeString = `${offsetToTimeString(startOffset, false)}`;
  }
  if (startOffset && endOffset) {
    timeString += ` - ${offsetToTimeString(endOffset, false)}`;
  }
  return timeString;
};

export const getMomentDate = (value: MomentInput) => {
  const momentDate = moment(value);
  const formatToken = momentDate.isSame(moment(), 'year')
    ? 'MMM D'
    : 'MMM D, YYYY';
  return momentDate.format(formatToken);
};

export const getMomentFromNow = (value: MomentInput) => {
  const now = moment();
  const momentDate = moment(value);
  const diffInSeconds = now.diff(value, 'seconds');
  return diffInSeconds >= 0 && diffInSeconds <= 44
    ? 'Just now'
    : momentDate.from(now);
};
